import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppBar, Box, Toolbar } from '@mui/material';
import clsx from 'clsx';

import { requestUserRegister } from '@atom/actions/userActions';
import Logo from '@atom/components/common/navigation/Logo';
import { useNavigation } from '@atom/hooks/useNavigation';
import { Icon, IconButton, TextField } from '@atom/mui';
import colors from '@atom/styles/colors';
import layoutStyles from '@atom/styles/layout';
import { RootState } from '@atom/types/store';
import accountUtilities from '@atom/utilities/accountUtilities';

import GoogleSocialLoginButton from './GoogleSocialLoginButton';
import LoginFooter from './LoginFooter';

import classes from './login.module.css';

const navBarStyle = {
  height: layoutStyles.topNavHeight,
  backgroundColor: colors.neutral.white,
  boxShadow: '0 2px 4px 1px',
  color: colors.utility.boxShadow,
  position: 'fixed',
  top: 0,
};

const styles = {
  activeIcon: {
    fontSize: '24px',
    color: colors.neutral.white,
  },
  inactiveIcon: {
    fontSize: '24px',
    color: colors.neutral.gray,
  },
  passwordField: {
    paddingTop: '0.4rem',
  },
};

interface RegistrationInput {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  phone: string;
}

interface ErrorsState {
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
  firstName: boolean;
  lastName: boolean;
}

const Registration = () => {
  const [, { navigate }] = useNavigation();
  const dispatch = useDispatch();
  const loading = useSelector(
    (state: RootState) => state.loading.loadingUserRegistration,
  );

  const [passwordVisible, setPasswordVisible] = useState<boolean>(false);
  const [input, setInput] = useState<RegistrationInput>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
  });
  const [errors, setErrors] = useState<ErrorsState>({
    email: false,
    confirmPassword: false,
    password: false,
    firstName: false,
    lastName: false,
  });

  const navigateToLogin = () => {
    navigate('/login');
  };

  const onChange = (property: keyof RegistrationInput, value: string) => {
    setInput(currentInput => ({
      ...currentInput,
      [property]: value,
    }));

    setErrors(currentErrors => ({
      ...currentErrors,
      [property]: false,
    }));
  };

  const validateValues = () => {
    const { email, firstName, lastName, password, confirmPassword } = input;

    const validEmail = accountUtilities.isValidEmail(email);
    const validFirstName = firstName;
    const validLastName = lastName;
    const validPassword = accountUtilities.isValidPassword(password);
    const validConfirmPassword =
      confirmPassword && password === confirmPassword;

    setErrors(currentErrors => ({
      ...currentErrors,
      email: !validEmail,
      firstName: !validFirstName,
      lastName: !validLastName,
      password: !validPassword,
      confirmPassword: !validConfirmPassword,
    }));

    return (
      validEmail &&
      validFirstName &&
      validLastName &&
      validPassword &&
      validConfirmPassword
    );
  };

  const submit = () => {
    const allValid = validateValues();

    if (allValid) {
      dispatch(
        requestUserRegister({
          firstName: input.firstName,
          lastName: input.lastName,
          email: input.email,
          password: input.password,
          phone: input.phone,
        }),
      );
    }
  };

  return (
    <>
      <AppBar style={navBarStyle}>
        <Toolbar>
          <div>
            <Logo />
          </div>
          <Box sx={{ flexGrow: 1 }} />
          <div className={classes.navRightContainer}>
            <div className={classes.navRightText}>Already have an account?</div>
            <div className={classes.navRightLogin} onClick={navigateToLogin}>
              Sign In
            </div>
          </div>
        </Toolbar>
      </AppBar>
      <div className={classes.container}>
        <div className={clsx(classes.authBackground, classes.base)} />
        <div className={classes.headingText}>Let's get started</div>
        <div className={classes.registrationIconContainer}>
          <div className={clsx(classes.registrationIcon, classes.active)}>
            <Icon style={styles.activeIcon}>person</Icon>
          </div>
          <div className={classes.registrationIcon}>
            <Icon style={styles.inactiveIcon}>email</Icon>
          </div>
          <div className={classes.registrationIcon}>
            <Icon style={styles.inactiveIcon}>business</Icon>
          </div>
        </div>
        <div className={classes.iconHorizontalBar} />
        <div className={classes.textFieldContainer}>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              value={input.firstName}
              label="First Name"
              disabled={loading}
              onChange={event => onChange('firstName', event.target.value)}
              helperText={errors.firstName ? 'Please enter a first name.' : ''}
              error={errors.firstName}
            />
          </div>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              value={input.lastName}
              label="Last Name"
              disabled={loading}
              onChange={event => onChange('lastName', event.target.value)}
              helperText={errors.lastName ? 'Please enter a last name.' : ''}
              error={errors.lastName}
            />
          </div>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              type="email"
              value={input.email}
              label="Email"
              disabled={loading}
              onChange={event => onChange('email', event.target.value)}
              helperText={
                errors.email ? 'Please enter a valid email address.' : ''
              }
              error={errors.email}
            />
          </div>
          <div className={clsx(classes.textFieldRegistration, 'fs-mask')}>
            <TextField
              variant="standard"
              style={styles.passwordField}
              type={passwordVisible ? 'text' : 'password'}
              value={input.password}
              label="Password"
              disabled={loading}
              onChange={event => onChange('password', event.target.value)}
              error={errors.password}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setPasswordVisible(currentValue => !currentValue)
                      }
                      size="small"
                    >
                      <Icon>
                        {passwordVisible ? 'visibility_off' : 'remove_red_eye'}
                      </Icon>
                    </IconButton>
                  ),
                },
              }}
            />
          </div>
          {errors.password && (
            <div className={classes.passwordError}>
              Please enter a password that is at least (12) characters long and
              includes at least one lowercase letter, uppercase letter, number,
              and special character (!@#$%^&*).
            </div>
          )}
          <div className={clsx(classes.textFieldRegistration, 'fs-mask')}>
            <TextField
              variant="standard"
              style={styles.passwordField}
              type={passwordVisible ? 'text' : 'password'}
              value={input.confirmPassword}
              label="Confirm Password"
              disabled={loading}
              onChange={event =>
                onChange('confirmPassword', event.target.value)
              }
              helperText={
                errors.confirmPassword
                  ? 'Passwords do not match. Please confirm your password.'
                  : ''
              }
              error={errors.confirmPassword}
              slotProps={{
                input: {
                  endAdornment: (
                    <IconButton
                      onClick={() =>
                        setPasswordVisible(currentValue => !currentValue)
                      }
                      size="small"
                    >
                      <Icon>
                        {passwordVisible ? 'visibility_off' : 'remove_red_eye'}
                      </Icon>
                    </IconButton>
                  ),
                },
              }}
            />
          </div>
          <div className={classes.textFieldRegistration}>
            <TextField
              variant="standard"
              value={input.phone}
              label="Phone Number (Optional)"
              disabled={loading}
              onChange={event => onChange('phone', event.target.value)}
            />
          </div>
        </div>
        <div className={classes.genericButtonContainer}>
          <div className={classes.genericButton} onClick={submit}>
            sign up
          </div>
        </div>
        <div className={classes.registrationSocialLoginContainer}>
          <div className={classes.loginDecisionBreak}>
            <span>or</span>
          </div>
          <div className={classes.socialLoginContainer}>
            <GoogleSocialLoginButton text="sign up with google" />
          </div>
        </div>
        <LoginFooter />
      </div>
    </>
  );
};

export default Registration;
